import React, { useState, useEffect } from 'react';
import './RevenueFilterBar.css';

const RevenueFilterBar = ({ onFiltersChange, currentFilters, availableFilters = { adPartners: [], adUnits: [], adSizes: [], geos: [], autoAdTypes: [] } }) => {
  const [showAdPartnerDropdown, setShowAdPartnerDropdown] = useState(false);
  const [showAdUnitDropdown, setShowAdUnitDropdown] = useState(false);
  const [showAdSizeDropdown, setShowAdSizeDropdown] = useState(false);
  const [showGeoDropdown, setShowGeoDropdown] = useState(false);
  const [showAutoAdTypeDropdown, setShowAutoAdTypeDropdown] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth <= 768) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleFilterChange = (filterType, value) => {
    const newFilters = {
      ...currentFilters,
      [filterType]: value
    };
    onFiltersChange(newFilters);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (currentFilters.adPartner && currentFilters.adPartner !== 'all') count++;
    if (currentFilters.adUnit !== 'all') count++;
    if (currentFilters.adSize !== 'all') count++;
    if (currentFilters.geo && currentFilters.geo !== 'all') count++;
    if (currentFilters.autoAdType && currentFilters.autoAdType !== 'all') count++;
    return count;
  };

  const clearFilters = () => {
    onFiltersChange({ adPartner: 'all', adUnit: 'all', adSize: 'all', geo: 'all', autoAdType: 'all' });
    setShowAdPartnerDropdown(false);
    setShowAdUnitDropdown(false);
    setShowAdSizeDropdown(false);
    setShowGeoDropdown(false);
    setShowAutoAdTypeDropdown(false);
  };

  // Close other dropdowns when one opens
  const handleDropdownToggle = (dropdownType) => {
    setShowAdPartnerDropdown(dropdownType === 'adPartner' ? !showAdPartnerDropdown : false);
    setShowAdUnitDropdown(dropdownType === 'adUnit' ? !showAdUnitDropdown : false);
    setShowAdSizeDropdown(dropdownType === 'adSize' ? !showAdSizeDropdown : false);
    setShowGeoDropdown(dropdownType === 'geo' ? !showGeoDropdown : false);
    setShowAutoAdTypeDropdown(dropdownType === 'autoAdType' ? !showAutoAdTypeDropdown : false);
  };

  return (
    <div className="revenue-filter-bar-compact">
      {/* Mobile Toggle Button */}
      {isMobile && (
        <button 
          className="filter-toggle-button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-label={isCollapsed ? 'Show filters' : 'Hide filters'}
        >
          <span className={`filter-icon filter-toggle-icon ${!isCollapsed ? 'filter-toggle-icon-open' : ''}`}>▼</span>
          <span>{isCollapsed ? 'Show Filters' : 'Hide Filters'}</span>
          {getActiveFiltersCount() > 0 && (
            <span className="filter-badge">{getActiveFiltersCount()}</span>
          )}
        </button>
      )}

      {/* Filter Content */}
      <div className={`filter-content ${isMobile && isCollapsed ? 'filter-collapsed' : ''}`}>
        <div className="filter-dropdown-group">
          {!isMobile && <span className="filter-label">Filters:</span>}
        
          {/* Ad Partner Dropdown - First in list */}
          {availableFilters.adPartners && availableFilters.adPartners.length > 0 && (
            <div className="filter-dropdown">
              <button 
                className={`filter-dropdown-button ${currentFilters.adPartner && currentFilters.adPartner !== 'all' ? 'active' : ''}`}
                onClick={() => handleDropdownToggle('adPartner')}
              >
                <span className="filter-icon">🏢</span>
                <span className="filter-text">{currentFilters.adPartner && currentFilters.adPartner !== 'all' ? currentFilters.adPartner : 'Ad Partner'}</span>
                <span className="dropdown-arrow">▼</span>
              </button>
              {showAdPartnerDropdown && (
                <div className="filter-dropdown-menu">
                  <button
                    className={`filter-dropdown-item ${currentFilters.adPartner === 'all' || !currentFilters.adPartner ? 'active' : ''}`}
                    onClick={() => {
                      handleFilterChange('adPartner', 'all');
                      setShowAdPartnerDropdown(false);
                    }}
                  >
                    <span className="filter-icon">🏢</span>
                    All Partners
                  </button>
                  {availableFilters.adPartners.map(partner => (
                    <button
                      key={partner}
                      className={`filter-dropdown-item ${currentFilters.adPartner === partner ? 'active' : ''}`}
                      onClick={() => {
                        handleFilterChange('adPartner', partner);
                        setShowAdPartnerDropdown(false);
                      }}
                    >
                      <span className="filter-icon">🏢</span>
                      {partner}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        
          {/* Ad Unit Dropdown */}
          {availableFilters.adUnits.length > 0 && (
            <div className="filter-dropdown">
              <button 
                className={`filter-dropdown-button ${currentFilters.adUnit !== 'all' ? 'active' : ''}`}
                onClick={() => handleDropdownToggle('adUnit')}
              >
                <span className="filter-icon">📊</span>
                <span className="filter-text">{currentFilters.adUnit !== 'all' ? currentFilters.adUnit : 'Ad Unit'}</span>
                <span className="dropdown-arrow">▼</span>
              </button>
              {showAdUnitDropdown && (
                <div className="filter-dropdown-menu">
                  <button
                    className={`filter-dropdown-item ${currentFilters.adUnit === 'all' ? 'active' : ''}`}
                    onClick={() => {
                      handleFilterChange('adUnit', 'all');
                      setShowAdUnitDropdown(false);
                    }}
                  >
                    <span className="filter-icon">📊</span>
                    All Ad Units
                  </button>
                  {availableFilters.adUnits.map(adUnit => (
                    <button
                      key={adUnit}
                      className={`filter-dropdown-item ${currentFilters.adUnit === adUnit ? 'active' : ''}`}
                      onClick={() => {
                        handleFilterChange('adUnit', adUnit);
                        setShowAdUnitDropdown(false);
                      }}
                    >
                      <span className="filter-icon">📊</span>
                      {adUnit}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Ad Size Dropdown */}
          {availableFilters.adSizes.length > 0 && (
            <div className="filter-dropdown">
              <button 
                className={`filter-dropdown-button ${currentFilters.adSize !== 'all' ? 'active' : ''}`}
                onClick={() => handleDropdownToggle('adSize')}
              >
                <span className="filter-icon">📐</span>
                <span className="filter-text">{currentFilters.adSize !== 'all' ? currentFilters.adSize : 'Ad Size'}</span>
                <span className="dropdown-arrow">▼</span>
              </button>
              {showAdSizeDropdown && (
                <div className="filter-dropdown-menu">
                  <button
                    className={`filter-dropdown-item ${currentFilters.adSize === 'all' ? 'active' : ''}`}
                    onClick={() => {
                      handleFilterChange('adSize', 'all');
                      setShowAdSizeDropdown(false);
                    }}
                  >
                    <span className="filter-icon">📐</span>
                    All Sizes
                  </button>
                  {availableFilters.adSizes.map(adSize => (
                    <button
                      key={adSize}
                      className={`filter-dropdown-item ${currentFilters.adSize === adSize ? 'active' : ''}`}
                      onClick={() => {
                        handleFilterChange('adSize', adSize);
                        setShowAdSizeDropdown(false);
                      }}
                    >
                      <span className="filter-icon">📐</span>
                      {adSize}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Geo Dropdown */}
          {availableFilters.geos.length > 0 && (
            <div className="filter-dropdown">
              <button 
                className={`filter-dropdown-button ${currentFilters.geo && currentFilters.geo !== 'all' ? 'active' : ''}`}
                onClick={() => handleDropdownToggle('geo')}
              >
                <span className="filter-icon">🗺️</span>
                <span className="filter-text">{currentFilters.geo && currentFilters.geo !== 'all' ? currentFilters.geo : 'Geo'}</span>
                <span className="dropdown-arrow">▼</span>
              </button>
              {showGeoDropdown && (
                <div className="filter-dropdown-menu">
                  <input
                    type="text"
                    placeholder="Search country..."
                    className="filter-text-input"
                    value={currentFilters.geo === 'all' ? '' : (currentFilters.geo || '')}
                    onChange={(e) => handleFilterChange('geo', e.target.value || 'all')}
                    style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', borderRadius: '4px', border: '1px solid #e5e7eb' }}
                    autoFocus
                  />
                  <button
                    className={`filter-dropdown-item ${currentFilters.geo === 'all' ? 'active' : ''}`}
                    onClick={() => {
                      handleFilterChange('geo', 'all');
                      setShowGeoDropdown(false);
                    }}
                  >
                    <span className="filter-icon">🌍</span>
                    All Countries
                  </button>
                  {availableFilters.geos.map(country => (
                    <button
                      key={country}
                      className={`filter-dropdown-item ${currentFilters.geo === country ? 'active' : ''}`}
                      onClick={() => {
                        handleFilterChange('geo', country);
                        setShowGeoDropdown(false);
                      }}
                    >
                      <span className="filter-icon">🌍</span>
                      {country}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Auto Ad Type Dropdown */}
          {availableFilters.autoAdTypes && availableFilters.autoAdTypes.length > 0 && (
            <div className="filter-dropdown">
              <button 
                className={`filter-dropdown-button ${currentFilters.autoAdType && currentFilters.autoAdType !== 'all' ? 'active' : ''}`}
                onClick={() => handleDropdownToggle('autoAdType')}
              >
                <span className="filter-icon">🤖</span>
                <span className="filter-text">{currentFilters.autoAdType && currentFilters.autoAdType !== 'all' ? currentFilters.autoAdType : 'Auto Ads'}</span>
                <span className="dropdown-arrow">▼</span>
              </button>
              {showAutoAdTypeDropdown && (
                <div className="filter-dropdown-menu">
                  <button
                    className={`filter-dropdown-item ${currentFilters.autoAdType === 'all' ? 'active' : ''}`}
                    onClick={() => {
                      handleFilterChange('autoAdType', 'all');
                      setShowAutoAdTypeDropdown(false);
                    }}
                  >
                    <span className="filter-icon">🤖</span>
                    All Auto Ads
                  </button>
                  {availableFilters.autoAdTypes.map(autoAdType => (
                    <button
                      key={autoAdType}
                      className={`filter-dropdown-item ${currentFilters.autoAdType === autoAdType ? 'active' : ''}`}
                      onClick={() => {
                        handleFilterChange('autoAdType', autoAdType);
                        setShowAutoAdTypeDropdown(false);
                      }}
                    >
                      <span className="filter-icon">🤖</span>
                      {autoAdType}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {getActiveFiltersCount() > 0 && (
          <button className="clear-filters" onClick={clearFilters}>
            Clear All
          </button>
        )}
      </div>
    </div>
  );
};

export default RevenueFilterBar;

